import { createComponentStore } from '../../utils/createComponentStore';

/**
 * Per-canvas-instance state for a custom_function node. One store per
 * componentId. The workflow-engine consumer reads three keys off
 * componentData (v2 schema, flat layout, schemaVersion=2):
 *   - customFunctionId   — which CustomFunction this node binds to
 *   - inputMappings      — paramName → literal value OR "${upstream.key}"
 *   - placeholders       — snapshot of outputSchema, with each entry
 *                          carrying both { key, schemaKey }. `key` is the
 *                          downstream reference token (${key}) and is
 *                          user-editable; `schemaKey` is what the function
 *                          code actually returns from Lambda and is fixed
 *                          by the function definition. The consumer maps
 *                          rawResult[schemaKey] → output[key] before
 *                          persisting the session data. Splitting these
 *                          two lets a tenant drop the same function
 *                          multiple times on one canvas without the
 *                          downstream ${greeting} tokens colliding.
 *
 * The function's code body is NOT copied into componentData — every
 * execution fetches the latest CustomFunction by id via
 * GET /private/custom-functions/{id}/for-workflow. That keeps all canvas
 * instances aligned with the latest saved definition.
 */
const initData = () => ({
  schemaVersion: 2,
  customFunctionId: null,
  inputMappings: {},
  placeholders: [],
});

const onLoadData = (rawData) => {
  const defaults = initData();
  const data = { ...defaults, ...(rawData || {}) };
  if (!data.inputMappings || typeof data.inputMappings !== 'object') {
    data.inputMappings = {};
  }
  if (!Array.isArray(data.placeholders)) {
    data.placeholders = [];
  }
  return data;
};

const { getStore: getCustomFunctionInstanceStore, defaultStore: useCustomFunctionInstanceStore } = createComponentStore({
  type: 'custom_function',
  endpoint: 'custom_function',
  registryKey: 'custom_function',
  initData,
  onLoadData,

  extraActions: (set, get) => ({
    setCustomFunctionId: (id) => {
      set({ data: { ...get().data, customFunctionId: id } });
    },

    setInputMapping: (paramName, value) => {
      const data = get().data;
      const inputMappings = { ...(data.inputMappings || {}), [paramName]: value };
      set({ data: { ...data, inputMappings } });
    },

    clearInputMapping: (paramName) => {
      const data = get().data;
      const inputMappings = { ...(data.inputMappings || {}) };
      delete inputMappings[paramName];
      set({ data: { ...data, inputMappings } });
    },

    // Derive the placeholders array from a function's outputSchema. Called
    // when the user picks a function (or when the function is reloaded
    // after an edit). Preserves user-renamed keys keyed on schemaKey —
    // schema changes on the function don't wipe the downstream ref a
    // tenant picked as long as the schema property still exists.
    syncPlaceholdersFromSchema: (outputSchema) => {
      if (!outputSchema || typeof outputSchema !== 'object') {
        set({ data: { ...get().data, placeholders: [] } });
        return;
      }
      const props = outputSchema.properties || {};
      const existingBySchemaKey = {};
      for (const p of get().data.placeholders || []) {
        const sk = p?.schemaKey ?? p?.key;
        if (sk) existingBySchemaKey[sk] = p;
      }
      const next = Object.keys(props).map((schemaKey) => {
        const schemaProp = props[schemaKey] || {};
        const existing = existingBySchemaKey[schemaKey];
        return {
          schemaKey,
          key: existing?.key || schemaKey,
          text: schemaProp.description || '',
          length: 1,
          style: {},
          type: mapJsonSchemaTypeToPlaceholder(schemaProp.type),
        };
      });
      set({ data: { ...get().data, placeholders: next } });
    },

    // Rename a placeholder by its schemaKey. The downstream reference
    // token is ${newKey}; rawResult[schemaKey] still carries the function
    // code's actual output. Backend consumer bridges the two at execute.
    // Writes schemaKey explicitly even when the entry was missing it (pre-
    // schemaKey-split instances) so the entry stays findable across
    // re-renders — otherwise the first keystroke would mutate key, the
    // lookup would no longer match, and the field would appear stuck.
    setPlaceholderKey: (schemaKey, newKey) => {
      const data = get().data;
      const placeholders = (data.placeholders || []).map((p) => {
        const sk = p.schemaKey ?? p.key;
        return sk === schemaKey ? { ...p, schemaKey, key: newKey } : p;
      });
      set({ data: { ...data, placeholders } });
    },
  }),
});

function mapJsonSchemaTypeToPlaceholder(jsonType) {
  switch (jsonType) {
    case 'integer':
    case 'number':
      return 'number';
    case 'object':
    case 'array':
      return 'json';
    case 'boolean':
    case 'string':
    default:
      return 'text';
  }
}

export { getCustomFunctionInstanceStore };
export default useCustomFunctionInstanceStore;
